import React, { useState, useEffect, SyntheticEvent } from 'react';

import { ILink, ILinkFields } from '@/types/contentful';

import { usePageDataContext } from '../../../../context/PageData';
import { getCookie } from 'cookies-next';

import {
  readOnetrustSettings,
  CONSENT_COOKIE_KEY,
  getConsentCookieValue
} from '../../../../utils/analytics/onetrust';
import { updateGTMConsent } from '../../../../utils/analytics/gtmConsent';
import { trackWithFingerprint } from '../../../../utils/trackWithFingerprint';
import { getCookieDomain } from '../../../../utils/cookies';

import { ConsentModal } from '../../../ConsentModal';
import Link from '../../../Link';

interface LegalNavLinksProps {
  isAffiliateFooter?: boolean;
  links?: ILink[];
  className?: string;
}

function readOptedOutFromCookie(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const cookieValue = getCookie(CONSENT_COOKIE_KEY);
    if (!cookieValue) {
      return false;
    }

    const cookieStr = cookieValue.toString();
    const settings = readOnetrustSettings(cookieStr);
    return settings?.C0004 === false;
  } catch {
    return false;
  }
}

function saveOptedOutCookie(optedOut: boolean): void {
  if (typeof window === 'undefined') return;

  const expiryDate = new Date();
  expiryDate.setMonth(expiryDate.getMonth() + 12);

  const cookieValue = getConsentCookieValue(!optedOut);
  const domain = getCookieDomain();
  const encodedValue = encodeURIComponent(cookieValue);

  const domainPart = domain ? `;domain=${domain}` : '';
  const cookieString = `${CONSENT_COOKIE_KEY}=${encodedValue};expires=${expiryDate.toUTCString()};path=/${domainPart}`;

  document.cookie = cookieString;
}

export function LegalNavLinks({
  isAffiliateFooter,
  links,
  className,
  ...props
}: LegalNavLinksProps) {
  const { cookies: { [CONSENT_COOKIE_KEY]: pvcyCookie } = {} } =
    usePageDataContext();

  const serverSettings = readOnetrustSettings(pvcyCookie);
  const serverOptedOut = serverSettings?.C0004 === false;

  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);

  const [optedOut, setOptedOut] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return readOptedOutFromCookie();
    }
    return serverOptedOut;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setOptedOut(readOptedOutFromCookie());
      updateGTMConsent();
    }
  }, []);

  useEffect(() => {
    if (isConsentModalOpen) {
      const currentOptedOut = readOptedOutFromCookie();
      setOptedOut(currentOptedOut);
    }
  }, [isConsentModalOpen]);

  useEffect(() => {
    updateGTMConsent();
  }, [optedOut]);

  const toggleOptOutModal = () => {
    const willOpen = !isConsentModalOpen;

    if (willOpen) {
      const currentOptedOut = readOptedOutFromCookie();
      setOptedOut(currentOptedOut);
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = '';
    }

    setIsConsentModalOpen(willOpen);
  };

  const handleToggleChange = (newOptedOut: boolean) => {
    setOptedOut(newOptedOut);
    saveOptedOutCookie(newOptedOut);
    updateGTMConsent();
  };

  const handleSave = () => {
    saveOptedOutCookie(optedOut);
    updateGTMConsent();
  };

  return (
    <div
      className={`flex flex-col ${className || ''}`}
      data-testid="legal-nav-list"
      {...props}
    >
      <ul className="-mx-2 list-none p-0">
        {links &&
          links.map(({ sys, fields }) => {
            const { title, url, openInNewTab, hasAffiliateLink }: ILinkFields =
              fields;
            const path = url ?? '';

            return title?.toLowerCase().includes('do not sell') ? (
              <li
                className="mb-3 block px-2 desktop:mb-1 desktop:inline-block desktop:border-r desktop:border-r-[#757575] desktop:last:border-r-0"
                data-testid="privacy-modal-link"
                key={sys?.id || title}
              >
                <Link
                  className="typography-body-2 font-medium leading-4 text-on-screen-surface-dark no-underline transition-colors duration-200 hover:text-[#00D9B4] hover:underline"
                  href="#"
                  onClick={(e: SyntheticEvent) => {
                    e.preventDefault();
                    trackWithFingerprint('Do Not Sell Selected', {
                      opted_out: optedOut
                    });
                    toggleOptOutModal();
                  }}
                >
                  {title}
                </Link>
              </li>
            ) : (
              <li
                key={sys?.id || title}
                className="mb-3 block px-2 desktop:mb-1 desktop:inline-block desktop:border-r desktop:border-r-[#757575] desktop:last:border-r-0"
              >
                <Link
                  className="typography-body-2 font-medium leading-4 text-on-screen-surface-dark no-underline transition-colors duration-200 hover:text-[#00D9B4] hover:underline"
                  href={
                    isAffiliateFooter && hasAffiliateLink
                      ? `/affiliates${path}`
                      : path
                  }
                  target={openInNewTab ? '_blank' : ''}
                  rel="noopener noreferrer"
                >
                  {title}
                </Link>
              </li>
            );
          })}
      </ul>
      {isConsentModalOpen && (
        <ConsentModal
          optedOut={optedOut}
          setOptedOut={handleToggleChange}
          toggleOptOutModal={toggleOptOutModal}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
